import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, CheckCircle, WarningCircle, Sparkle, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  info: {
    icon: Info,
    bg: 'bg-blue-500/10 border-blue-500/30',
    iconColor: 'text-blue-400',
  },
  success: {
    icon: CheckCircle,
    bg: 'bg-green-500/10 border-green-500/30',
    iconColor: 'text-green-400',
  },
  warning: {
    icon: WarningCircle,
    bg: 'bg-amber-500/10 border-amber-500/30',
    iconColor: 'text-amber-400',
  },
  announcement: {
    icon: Sparkle,
    bg: 'bg-blue-500/10 border-blue-500/30',
    iconColor: 'text-blue-400',
  },
};

/**
 * Banner that shows active broadcast messages at the top of every page.
 * Only renders when user is authenticated and has undismissed broadcasts.
 */
const BroadcastBanner = () => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) { setItems([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/broadcasts/active`);
        if (!cancelled) setItems(res.data);
      } catch {}
    };
    load();
  }, [isAuthenticated]);

  const dismiss = async (id) => {
    // Optimistic remove
    setItems(prev => prev.filter(b => b.id !== id));
    try {
      await axios.post(`${API}/api/broadcasts/${id}/dismiss`);
    } catch {}
  };

  if (!items.length) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-40 pointer-events-none" data-testid="broadcast-banner">
      <div className="max-w-5xl mx-auto px-4 pt-3 space-y-2">
        <AnimatePresence>
          {items.map(b => {
            const s = SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.info;
            const Icon = s.icon;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`pointer-events-auto rounded-xl border backdrop-blur-xl p-3 flex items-start gap-3 ${s.bg}`}
                data-testid={`broadcast-banner-${b.id}`}
              >
                <Icon weight="fill" className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white">{b.title}</p>
                  <p className="text-sm text-slate-300 mt-0.5 whitespace-pre-line">{b.body}</p>
                  {b.cta_label && b.cta_url && (
                    <a href={b.cta_url}
                       className={`inline-flex items-center gap-1 mt-2 text-sm font-semibold ${s.iconColor} hover:underline`}
                       data-testid={`broadcast-cta-${b.id}`}>
                      {b.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <button onClick={() => dismiss(b.id)}
                  className="flex-shrink-0 p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  data-testid={`broadcast-dismiss-${b.id}`}
                  aria-label="Dismiss">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BroadcastBanner;
